import type { ReactNode } from 'react';
import { useRevalidator } from 'react-router-dom';
import { Button, EmptyState } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { setIncludeTestWorkspaces, useIncludeTestWorkspaces } from '@/lib/test-workspaces/include-test-workspaces';
import { FilterPill } from '@/components/filter-bar';
import { ShieldIcon } from '@/components/layout/icons';

/*
 * The three visible pieces of the operator's test-workspace filter
 * (IMM-593.2): the pill every workspace-scoped list carries, the badge a
 * test-workspace row wears while the filter is on, and the empty state a list
 * shows while the filter is hiding rows. All read one shared store, so the pill
 * is already active on the next list an operator opens.
 */

const FILTERS_NS = 'pages.admin.shell.filters';

/**
 * Toggles the shared filter and re-runs the current route's loader, so the list
 * refetches under the new state and — for keyset lists, whose loader effect
 * resets rows + cursor — restarts from the first page rather than reusing a
 * cursor cut under the other state.
 */
function useToggleIncludeTestWorkspaces(): () => void {
  const active = useIncludeTestWorkspaces();
  const revalidator = useRevalidator();

  return () => {
    setIncludeTestWorkspaces(!active);
    void revalidator.revalidate();
  };
}

/** The test-workspaces pill — the existing `FilterPill`, wired to the shared filter. */
export function TestWorkspacesPill() {
  const t = useTranslations(FILTERS_NS);
  const active = useIncludeTestWorkspaces();
  const toggle = useToggleIncludeTestWorkspaces();

  return (
    <span title={t('test-workspaces-aria')}>
      <FilterPill icon={ShieldIcon} label={t('test-workspaces')} active={active} onToggle={toggle} />
    </span>
  );
}

/**
 * The `Test` badge on a row (or a detail header) that belongs to a test
 * workspace. Renders nothing for a real workspace, so callers pass `isTest`
 * straight through and never branch themselves.
 */
export function TestBadge({ isTest, className }: { readonly isTest: boolean; readonly className?: string }) {
  const t = useTranslations(FILTERS_NS);

  if (!isTest) return null;

  const full = t('test-badge-full');

  return (
    <span
      title={full}
      aria-label={full}
      className={`inline-flex shrink-0 items-center rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-700 ${className ?? ''}`}
    >
      {t('test-badge')}
    </span>
  );
}

/**
 * The empty state a workspace-scoped list shows when it has no rows AND the
 * filter is inactive: names the filter and offers to switch it on from right
 * here (AC 31–33). Shown whenever the list is empty, without probing for hidden
 * rows — an operator who is hiding test data is always entitled to know it.
 */
export function TestHiddenEmptyState() {
  const t = useTranslations(FILTERS_NS);
  const toggle = useToggleIncludeTestWorkspaces();

  return (
    <div className="p-6">
      <EmptyState
        title={t('empty-test-hidden')}
        action={
          <Button size="sm" variant="secondary" onClick={toggle}>
            {t('empty-test-hidden-cta')}
          </Button>
        }
      />
    </div>
  );
}

/**
 * The empty state of a workspace-scoped list, in one place for the eight of
 * them: the test-hidden state while the filter is inactive and the server
 * returned nothing, the list's own `title` otherwise.
 *
 * `serverEmpty` is for lists that ALSO filter client-side (a search box, type
 * pills): pass whether the LOADER data is empty, so a no-match search on a
 * populated list is not blamed on hidden test data (BL-053) — the empty state
 * must read the collection the table body renders. Server-filtered lists omit
 * it; their rows are the loader data.
 */
export function ListEmptyState({
  title,
  serverEmpty = true,
}: {
  readonly title: string;
  readonly serverEmpty?: boolean;
}) {
  const includeTestWorkspaces = useIncludeTestWorkspaces();

  if (serverEmpty && !includeTestWorkspaces) return <TestHiddenEmptyState />;

  return (
    <div className="p-6">
      <EmptyState title={title} />
    </div>
  );
}

/** A workspace (or Standort) label with the `Test` badge beside it — the cell every list renders. */
export function WorkspaceLabel({ isTest, children }: { readonly isTest: boolean; readonly children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2">
      {children}
      <TestBadge isTest={isTest} />
    </span>
  );
}
